import z from 'zod'

// IMPORTANT: Every field (including nested object schemas) MUST have a .catch() default.
// Saved configs may predate newly added fields. Without .catch(), parsing will fail
// for users whose data was saved before the field existed. This applies to renames too —
// the old key becomes unknown and gets stripped, so the new key must catch gracefully.
//
// NOTE: When modifying this schema, update canvas-schema.json to match.

const dimensionSchema = z.object({
	mode: z.enum(['px', '%']).catch('px'),
	pxValue: z.number().catch(100),
	percentValue: z.number().catch(100),
}).catch({ mode: 'px', pxValue: 100, percentValue: 100 })

const floatAnchorSchema = z.enum([
	'top-left',
	'top-center',
	'top-right',
	'center-left',
	'center',
	'center-right',
	'bottom-left',
	'bottom-center',
	'bottom-right',
]).catch('top-left')

const baseLayerSchema = z.object({
	id: z.string(),
	hidden: z.boolean().catch(false),
	locked: z.boolean().catch(false),
})

// Not a z.object() — just a bag of fields spread into leaf layer schemas.
// No .catch() needed here; each individual field already has one.
const floatFields = {
	floating: z.boolean().catch(false),
	floatAnchor: floatAnchorSchema,
	floatX: dimensionSchema,
	floatY: dimensionSchema,
}

const spacingSchema = z.object({
	paddingLeft: z.number().catch(0),
	paddingRight: z.number().catch(0),
	paddingTop: z.number().catch(0),
	paddingBottom: z.number().catch(0),

	marginLeft: z.number().catch(0),
	marginRight: z.number().catch(0),
	marginTop: z.number().catch(0),
	marginBottom: z.number().catch(0),
}).catch({
	paddingLeft: 0,
	paddingRight: 0,
	paddingTop: 0,
	paddingBottom: 0,
	marginLeft: 0,
	marginRight: 0,
	marginTop: 0,
	marginBottom: 0,
})

const textColorSchema = z.object({
	mode: z.enum(['foregroundColor', 'backgroundColor', 'accentForegroundColor', 'accentBackgroundColor', 'color'])
		.catch('foregroundColor'),
	color: z.string().catch('#000000'),
}).catch({ mode: 'foregroundColor', color: '#000000' })

const maskSchema = z.object({
	maskMode: z.enum(['none', 'solid', 'gradient']).catch('none'),
	blendMode: z.enum([
		'normal',
		'multiply',
		'screen',
		'overlay',
		'darken',
		'lighten',
		'color-dodge',
		'color-burn',
		'hard-light',
		'soft-light',
		'difference',
		'exclusion',
		'hue',
		'saturation',
		'color',
		'luminosity',
	]).catch('normal'),
	solidColor: textColorSchema,
	solidOpacity: z.number().catch(50),
	gradientColorStart: textColorSchema,
	gradientColorEnd: textColorSchema,
	gradientOpacityStart: z.number().catch(100),
	gradientOpacityEnd: z.number().catch(0),
	gradientAngle: z.number().catch(0),
}).catch({
	maskMode: 'none',
	blendMode: 'normal',
	solidColor: { mode: 'foregroundColor', color: '#000000' },
	solidOpacity: 50,
	gradientColorStart: { mode: 'foregroundColor', color: '#000000' },
	gradientColorEnd: { mode: 'foregroundColor', color: '#000000' },
	gradientOpacityStart: 100,
	gradientOpacityEnd: 0,
	gradientAngle: 0,
})

const backgroundSchema = z.object({
	mode: z.enum([
		'transparent',
		'foregroundColor',
		'backgroundColor',
		'accentForegroundColor',
		'accentBackgroundColor',
		'color',
		'image',
		'externalImage',
	]).catch('transparent'),
	color: z.string().catch('#000000'),
	imageUrl: z.string().catch(''),
	imageScale: z.number().min(10).catch(100),
	imageOffsetX: z.number().catch(50),
	imageOffsetY: z.number().catch(50),
}).catch({ mode: 'transparent', color: '#000000', imageUrl: '', imageScale: 100, imageOffsetX: 50, imageOffsetY: 50 })

const textHighlightSchema = z.object({
	style: z.enum(['none', 'marker', 'underline', 'brush']).catch('none'),
	color: textColorSchema,
	opacity: z.number().int().min(0).max(100).catch(100),
	paddingX: z.number().catch(10),
	paddingY: z.number().catch(2),
	borderRadius: z.number().catch(0), // only used by "marker"
	thickness: z.number().min(1).max(100).catch(35), // only used by "underline"
	offsetY: z.number().catch(0), // only used by "underline"
}).catch({
	style: 'none',
	color: { mode: 'accentBackgroundColor', color: '#ffe14d' },
	opacity: 100,
	paddingX: 10,
	paddingY: 2,
	borderRadius: 0,
	thickness: 35,
	offsetY: 0,
})

export const NO_TEXT_HIGHLIGHT = Object.freeze({
	style: 'none' as const,
	color: { mode: 'accentBackgroundColor' as const, color: '#ffe14d' },
	opacity: 100,
	paddingX: 10,
	paddingY: 2,
	borderRadius: 0,
	thickness: 35,
	offsetY: 0,
})

const textShadowSimpleSchema = z.object({
	opacity: z.number().catch(20),
	spread: z.number().catch(5),
}).catch({ opacity: 20, spread: 5 })

const textStyleSchema = z.object({
	textColorMode: z.enum(['auto', 'value']).catch('auto'),
	textColor: textColorSchema,
	fontFamilyMode: z.enum(['auto', 'value']).catch('auto'),
	fontFamilyProvider: z.enum(['system', 'google-fonts', 'custom']).catch('system'),
	fontFamilyGoogleFamily: z.string().catch(''),
	fontFamilyCustomUrl: z.string().catch(''),
	fontFamilyCustomName: z.string().catch(''),
	fontSizeMode: z.enum(['auto', 'value']).catch('auto'),
	fontSizeUnit: z.enum(['px']).catch('px'),
	fontSizePxValue: z.number().catch(16),
	fontWeightMode: z.enum(['auto', 'value']).catch('auto'),
	fontWeightValue: z.number().catch(400),
	fontStyleMode: z.enum(['auto', 'value']).catch('auto'),
	fontStyleValue: z.enum(['normal', 'italic']).catch('normal'),
	lineHeightMode: z.enum(['auto', 'value']).catch('auto'),
	lineHeightValue: z.number().catch(1.4),
	textShadowMode: z.enum(['auto', 'value']).catch('auto'),
	textShadowValue: z.enum(['none', 'simple']).catch('simple'),
	textShadowSimple: textShadowSimpleSchema,
}).catch({
	textColorMode: 'auto',
	textColor: { mode: 'foregroundColor', color: '#000000' },
	fontFamilyMode: 'auto',
	fontFamilyProvider: 'system',
	fontFamilyGoogleFamily: '',
	fontFamilyCustomUrl: '',
	fontFamilyCustomName: '',
	fontSizeMode: 'auto',
	fontSizeUnit: 'px',
	fontSizePxValue: 16,
	fontWeightMode: 'auto',
	fontWeightValue: 400,
	fontStyleMode: 'auto',
	fontStyleValue: 'normal',
	lineHeightMode: 'auto',
	lineHeightValue: 1.4,
	textShadowMode: 'auto',
	textShadowValue: 'simple',
	textShadowSimple: { opacity: 20, spread: 5 },
})

export const ALL_BORDER_RADIUS_CORNERS = Object.freeze({
	topLeft: true,
	topRight: true,
	bottomRight: true,
	bottomLeft: true,
})

const borderRadiusCornersSchema = z.object({
	topLeft: z.boolean().catch(true),
	topRight: z.boolean().catch(true),
	bottomRight: z.boolean().catch(true),
	bottomLeft: z.boolean().catch(true),
}).catch(() => ({ ...ALL_BORDER_RADIUS_CORNERS }))

export type BorderRadiusCorners = z.infer<typeof borderRadiusCornersSchema>
export type BorderRadiusCorner = keyof BorderRadiusCorners

// Opacities are expressed as whole percentages, not CSS 0–1 fractions.
const opacitySchema = z.number().int().min(0).max(100).catch(100)

/**
 * Width does NOT use the opt-in/inherit pattern (mode: 'auto' | 'value').
 * Unlike text properties, width is not inheritable — 'auto' here is a regular
 * value choice meaning "no explicit width", not "inherit from parent".
 * So the mode enum directly lists the available units alongside 'auto'.
 */
const widthSchema = z.object({
	mode: z.enum(['auto', 'px', '%']).catch('auto'),
	pxValue: z.number().catch(100),
	percentValue: z.number().catch(100),
}).catch({ mode: 'auto', pxValue: 100, percentValue: 100 })

// Layer schemas don't need a top-level .catch() — they're matched by discriminated
// union on `type`, which must parse exactly. Every other field has its own .catch().
const textLayerSchema = baseLayerSchema.extend({
	...floatFields,
	type: z.literal('text'),
	content: z.enum(['ref', 'custom']).catch('custom'),
	refTo: z.enum(['title', 'subtitle', 'cta', 'location']).catch('title'),
	customValue: z.string().catch('Your text here'),
	textAlignMode: z.enum(['auto', 'value']).catch('auto'),
	textAlignValue: z.enum(['left', 'center', 'right']).catch('left'),
	spacing: spacingSchema,
	background: backgroundSchema,
	textStyle: textStyleSchema,
	textHighlight: textHighlightSchema,
	borderRadius: z.number().catch(0),
	borderRadiusCorners: borderRadiusCornersSchema,
})

const shapeLayerSchema = baseLayerSchema.extend({
	...floatFields,
	type: z.literal('shape'),
	shapeKind: z.enum(['rectangle', 'preset']).catch('rectangle'),
	preset: z.enum(['locationPin', 'heart', 'badge']).catch('locationPin'),
	// "original" is different from "auto" in other inheritable values; it means "this layer supplies
	// no fill" — a rectangle then draws no shape, a preset takes the inherited text colour, an SVG
	// keeps its own colours
	fillMode: z.enum(['original', 'value']).catch('original'),
	fill: textColorSchema,
	shapeFit: z.enum(['contain', 'stretch']).catch('contain'),
	width: dimensionSchema,
	height: dimensionSchema,

	// shapes support both a fill and a background; backgrounds work like any other block background,
	// while fills apply to the shape itself. This means you can use the background and its radius to
	// make badges with rounded corners.
	background: backgroundSchema,
	borderRadius: z.number().catch(0),
	borderRadiusCorners: borderRadiusCornersSchema,
	spacing: spacingSchema,
})

const imageLayerSchema = baseLayerSchema.extend({
	...floatFields,
	type: z.literal('image'),
	content: z.enum(['ref', 'custom']).catch('custom'),
	refTo: z.enum(['logo', 'platformLogo']).catch('logo'),
	customValue: z.string().catch(''),

	width: widthSchema,
	spacing: spacingSchema,
	background: backgroundSchema,
	borderRadius: z.number().catch(0),
	borderRadiusCorners: borderRadiusCornersSchema,
	opacity: opacitySchema,
})

// grouping layers

const substackLayerSchema = baseLayerSchema.extend({
	...floatFields,
	type: z.literal('substack'),
	direction: z.enum(['row', 'column']).catch('column'),
	alignItems: z.enum(['start', 'center', 'end', 'stretch']).catch('stretch'),
	justifyContent: z.enum(['start', 'center', 'end', 'space-between']).catch('start'),
	childSizing: z.enum(['auto', 'equal']).catch('auto'),
	widthSizing: z.enum(['auto', 'fill']).catch('auto'),
	gap: z.number().catch(0),
	fontFamilyMode: z.enum(['auto', 'value']).catch('auto'),
	fontFamilyProvider: z.enum(['system', 'google-fonts', 'custom']).catch('system'),
	fontFamilyGoogleFamily: z.string().catch(''),
	fontFamilyCustomUrl: z.string().catch(''),
	fontFamilyCustomName: z.string().catch(''),
	spacing: spacingSchema,
	background: backgroundSchema,
	borderRadius: z.number().catch(0),
	borderRadiusCorners: borderRadiusCornersSchema,
})

// top level layers

const contentLayerSchema = baseLayerSchema.extend({
	type: z.literal('content'),
	xPin: z.enum(['left', 'center', 'right', 'stretch']).catch('center'),
	yPin: z.enum(['top', 'center', 'bottom']).catch('top'),
	textAlignMode: z.enum(['auto', 'value']).catch('auto'),
	textAlignValue: z.enum(['left', 'center', 'right']).catch('left'),
	spacing: spacingSchema,
	gap: z.number().catch(0),
	textStyle: textStyleSchema,
})

const backdropLayerSchema = baseLayerSchema.extend({
	type: z.literal('backdrop'),
	background: backgroundSchema,
	mask: maskSchema,
})

// the document itself

export type LayerNode = {
	id: string
	children: LayerNode[]
}

export const layerNodeSchema: z.ZodType<LayerNode> = z.object({
	id: z.string(),
	children: z.lazy(() => z.array(layerNodeSchema)),
})

const layerSchema = z.discriminatedUnion('type', [
	contentLayerSchema,
	backdropLayerSchema,
	substackLayerSchema,
	textLayerSchema,
	shapeLayerSchema,
	imageLayerSchema,
])

export type Layer = z.infer<typeof layerSchema>
export type LeafLayer = Extract<Layer, { floating: boolean }>
export type LayerType = Layer['type']
export type TextContentMode = z.infer<typeof textLayerSchema>['content']
export type TextRefTarget = z.infer<typeof textLayerSchema>['refTo']
export type TextAlignMode = z.infer<typeof textLayerSchema>['textAlignMode']
export type TextAlignValue = z.infer<typeof textLayerSchema>['textAlignValue']
export type ImageContentMode = z.infer<typeof imageLayerSchema>['content']
export type ImageRefTarget = z.infer<typeof imageLayerSchema>['refTo']
export type ImageWidth = z.infer<typeof widthSchema>
export type ImageWidthMode = ImageWidth['mode']
export type ShapeKind = z.infer<typeof shapeLayerSchema>['shapeKind']
export type ShapePreset = z.infer<typeof shapeLayerSchema>['preset']
export type ShapeFillMode = z.infer<typeof shapeLayerSchema>['fillMode']
export type ShapeFit = z.infer<typeof shapeLayerSchema>['shapeFit']
export type Spacing = z.infer<typeof spacingSchema>
export type TextStyle = z.infer<typeof textStyleSchema>
export type TextHighlight = z.infer<typeof textHighlightSchema>
export type TextHighlightStyle = TextHighlight['style']
export type TextColor = z.infer<typeof textColorSchema>
export type TextColorMode = TextColor['mode']
export type Background = z.infer<typeof backgroundSchema>
export type BackgroundMode = Background['mode']
export type ContentXPin = z.infer<typeof contentLayerSchema>['xPin']
export type ContentYPin = z.infer<typeof contentLayerSchema>['yPin']
export type SubstackDirection = z.infer<typeof substackLayerSchema>['direction']
export type SubstackAlignItems = z.infer<typeof substackLayerSchema>['alignItems']
export type SubstackJustifyContent = z.infer<typeof substackLayerSchema>['justifyContent']
export type SubstackChildSizing = z.infer<typeof substackLayerSchema>['childSizing']
export type SubstackWidthSizing = z.infer<typeof substackLayerSchema>['widthSizing']
export type Dimension = z.infer<typeof dimensionSchema>
export type DimensionMode = Dimension['mode']
export type FontFamilyMode = TextStyle['fontFamilyMode']
export type FontFamilyProvider = TextStyle['fontFamilyProvider']
export type TextShadowMode = TextStyle['textShadowMode']
export type TextShadowValue = TextStyle['textShadowValue']
export type TextShadowSimple = z.infer<typeof textShadowSimpleSchema>
export type FloatAnchor = z.infer<typeof floatAnchorSchema>
export type Mask = z.infer<typeof maskSchema>
export type MaskMode = Mask['maskMode']
export type MaskBlendMode = Mask['blendMode']

export const CONTAINER_TYPES = new Set<LayerType>(['content', 'substack'])
export const LEAF_TYPES = new Set<LayerType>(['text', 'image', 'shape'])
export const FLOATABLE_TYPES = new Set<LayerType>(['text', 'image', 'shape', 'substack'])

export const BASE_FLOAT_DEFAULTS = {
	floating: false,
	floatAnchor: 'top-left' as const,
	floatX: { mode: 'px' as const, pxValue: 0, percentValue: 0 },
	floatY: { mode: 'px' as const, pxValue: 0, percentValue: 0 },
}

const LAYER_DEFAULTS: Record<LayerType, (id: string) => Layer> = {
	content: (id) => ({
		type: 'content',
		id,
		hidden: false,
		locked: false,
		xPin: 'center',
		yPin: 'top',
		textAlignMode: 'auto',
		textAlignValue: 'left',
		spacing: {
			paddingLeft: 0,
			paddingRight: 0,
			paddingTop: 0,
			paddingBottom: 0,
			marginLeft: 0,
			marginRight: 0,
			marginTop: 0,
			marginBottom: 0,
		},
		gap: 0,
		textStyle: {
			textColorMode: 'auto',
			textColor: { mode: 'foregroundColor', color: '#000000' },
			fontFamilyMode: 'auto',
			fontFamilyProvider: 'system',
			fontFamilyGoogleFamily: '',
			fontFamilyCustomUrl: '',
			fontFamilyCustomName: '',
			fontSizeMode: 'auto',
			fontSizeUnit: 'px',
			fontSizePxValue: 16,
			fontWeightMode: 'auto',
			fontWeightValue: 400,
			fontStyleMode: 'auto',
			fontStyleValue: 'normal',
			lineHeightMode: 'auto',
			lineHeightValue: 1.4,
			textShadowMode: 'auto',
			textShadowValue: 'simple',
			textShadowSimple: { opacity: 20, spread: 5 },
		},
	}),
	backdrop: (id) => ({
		type: 'backdrop',
		id,
		hidden: false,
		locked: false,
		background: {
			mode: 'transparent',
			color: '#000000',
			imageUrl: '',
			imageScale: 100,
			imageOffsetX: 50,
			imageOffsetY: 50,
		},
		mask: {
			maskMode: 'none',
			blendMode: 'normal',
			solidColor: { mode: 'foregroundColor', color: '#000000' },
			solidOpacity: 50,
			gradientColorStart: { mode: 'foregroundColor', color: '#000000' },
			gradientColorEnd: { mode: 'foregroundColor', color: '#000000' },
			gradientOpacityStart: 100,
			gradientOpacityEnd: 0,
			gradientAngle: 0,
		},
	}),
	substack: (id) => ({
		type: 'substack',
		id,
		hidden: false,
		locked: false,
		...BASE_FLOAT_DEFAULTS,
		direction: 'column',
		alignItems: 'stretch',
		justifyContent: 'start',
		childSizing: 'auto',
		widthSizing: 'auto',
		gap: 0,
		fontFamilyMode: 'auto',
		fontFamilyProvider: 'system',
		fontFamilyGoogleFamily: '',
		fontFamilyCustomUrl: '',
		fontFamilyCustomName: '',
		spacing: {
			paddingLeft: 0,
			paddingRight: 0,
			paddingTop: 0,
			paddingBottom: 0,
			marginLeft: 0,
			marginRight: 0,
			marginTop: 0,
			marginBottom: 0,
		},
		background: {
			mode: 'transparent',
			color: '#000000',
			imageUrl: '',
			imageScale: 100,
			imageOffsetX: 50,
			imageOffsetY: 50,
		},
		borderRadius: 0,
		borderRadiusCorners: { ...ALL_BORDER_RADIUS_CORNERS },
	}),
	text: (id) => ({
		type: 'text',
		id,
		hidden: false,
		locked: false,
		...BASE_FLOAT_DEFAULTS,
		content: 'custom',
		refTo: 'title',
		customValue: 'Your text here',
		textAlignMode: 'auto',
		textAlignValue: 'left',
		spacing: {
			paddingLeft: 0,
			paddingRight: 0,
			paddingTop: 0,
			paddingBottom: 0,
			marginLeft: 0,
			marginRight: 0,
			marginTop: 0,
			marginBottom: 0,
		},
		background: {
			mode: 'transparent',
			color: '#000000',
			imageUrl: '',
			imageScale: 100,
			imageOffsetX: 50,
			imageOffsetY: 50,
		},
		textStyle: {
			textColorMode: 'auto',
			textColor: { mode: 'foregroundColor', color: '#000000' },
			fontFamilyMode: 'auto',
			fontFamilyProvider: 'system',
			fontFamilyGoogleFamily: '',
			fontFamilyCustomUrl: '',
			fontFamilyCustomName: '',
			fontSizeMode: 'auto',
			fontSizeUnit: 'px',
			fontSizePxValue: 16,
			fontWeightMode: 'auto',
			fontWeightValue: 400,
			fontStyleMode: 'auto',
			fontStyleValue: 'normal',
			lineHeightMode: 'auto',
			lineHeightValue: 1.4,
			textShadowMode: 'auto',
			textShadowValue: 'simple',
			textShadowSimple: { opacity: 20, spread: 5 },
		},
		textHighlight: { ...NO_TEXT_HIGHLIGHT, color: { ...NO_TEXT_HIGHLIGHT.color } },
		borderRadius: 0,
		borderRadiusCorners: { ...ALL_BORDER_RADIUS_CORNERS },
	}),
	shape: (id) => ({
		type: 'shape',
		id,
		hidden: false,
		locked: false,
		...BASE_FLOAT_DEFAULTS,
		shapeKind: 'rectangle',
		preset: 'locationPin',
		// A rectangle with no fill draws nothing, so a new shape would land invisible.
		fillMode: 'value',
		fill: { mode: 'foregroundColor', color: '#000000' },
		shapeFit: 'contain',
		width: { mode: 'px', pxValue: 100, percentValue: 100 },
		height: { mode: 'px', pxValue: 100, percentValue: 100 },
		background: {
			mode: 'transparent',
			color: '#000000',
			imageUrl: '',
			imageScale: 100,
			imageOffsetX: 50,
			imageOffsetY: 50,
		},
		borderRadius: 0,
		borderRadiusCorners: { ...ALL_BORDER_RADIUS_CORNERS },
		spacing: {
			paddingLeft: 0,
			paddingRight: 0,
			paddingTop: 0,
			paddingBottom: 0,
			marginLeft: 0,
			marginRight: 0,
			marginTop: 0,
			marginBottom: 0,
		},
	}),
	image: (id) => ({
		type: 'image',
		id,
		hidden: false,
		locked: false,
		...BASE_FLOAT_DEFAULTS,
		content: 'custom',
		refTo: 'logo',
		customValue: '',
		width: { mode: 'auto', pxValue: 100, percentValue: 100 },
		spacing: {
			paddingLeft: 0,
			paddingRight: 0,
			paddingTop: 0,
			paddingBottom: 0,
			marginLeft: 0,
			marginRight: 0,
			marginTop: 0,
			marginBottom: 0,
		},
		background: {
			mode: 'transparent',
			color: '#000000',
			imageUrl: '',
			imageScale: 100,
			imageOffsetX: 50,
			imageOffsetY: 50,
		},
		borderRadius: 0,
		borderRadiusCorners: { ...ALL_BORDER_RADIUS_CORNERS },
		opacity: 100,
	}),
}

export function createLayerDefaults(type: LayerType, id: string): Layer {
	return LAYER_DEFAULTS[type](id)
}

/**
 * Strip layers with unknown types (e.g. any removed types) from raw data
 * before Zod validates, so old saved configs don't fail to parse.
 */
function migrateRawData(raw: Record<string, unknown>): Record<string, unknown> {
	const layers = raw.layers
	if (!layers || typeof layers !== 'object') { return raw }

	const knownTypes = new Set<string>(layerSchema.options.map((option) => option.shape.type.value))
	const layerRecord = layers as Record<string, Record<string, unknown>>
	const removedIds = new Set<string>()

	const migratedLayers: Record<string, unknown> = {}
	for (const [id, layer] of Object.entries(layerRecord)) {
		if (layer && knownTypes.has(layer.type as string)) {
			migratedLayers[id] = layer
		} else {
			removedIds.add(id)
		}
	}

	if (removedIds.size === 0) { return raw }

	type RawTreeNode = { id?: string; children?: RawTreeNode[] }
	function pruneTree(nodes: RawTreeNode[]): RawTreeNode[] {
		return nodes
			.filter((n) => !removedIds.has(n.id ?? ''))
			.map((n) => ({ ...n, children: pruneTree(n.children ?? []) }))
	}

	return {
		...raw,
		layers: migratedLayers,
		layerTree: Array.isArray(raw.layerTree) ? pruneTree(raw.layerTree) : raw.layerTree,
	}
}

export const CanvasDataV1 = z.preprocess(
	(val) => (val && typeof val === 'object' ? migrateRawData(val as Record<string, unknown>) : val),
	z.object({
		version: z.literal(1),
		layers: z.record(z.string(), layerSchema),
		layerTree: z.array(layerNodeSchema),
	}),
)
